import { z } from 'zod';
import { estDatePure, jourDansFuseau } from './jour.js';
import { estChampDeTrace } from './base.js';

export type Condition =
  | { et: Condition[] }
  | { ou: Condition[] }
  | { champ: string; op: 'avantAujourdhui' }
  | { champ: string; op: 'aujourdhui' }
  | { champ: string; op: 'vide'; type?: 'texte' }
  | { champ: string; op: 'rempli'; type?: 'texte' }
  | { champ: string; op: 'hors'; valeurs: (string | number | boolean)[] }
  | { champ: string; op: 'parmi'; valeurs: (string | number | boolean)[] }
  | { champ: string; op: 'moi' };

const champ = z.string().regex(/^(?:socle:)?[a-z][a-zA-Z0-9_]*$/);
// Une borne a la lecture, avant de laisser Zod descendre dans une declaration.
export function profondeurDeclaree(value: unknown, profondeur = 0): boolean {
  if (profondeur > 8) return false;
  if (!value || typeof value !== 'object') return true;
  return Object.values(value).every(child => profondeurDeclaree(child, profondeur + 1));
}
const arbre: z.ZodType<Condition> = z.lazy(() => z.union([
  z.object({ et: z.array(arbre).min(1).max(32) }).strict(),
  z.object({ ou: z.array(arbre).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('avantAujourdhui') }).strict(),
  z.object({ champ, op: z.literal('aujourdhui') }).strict(),
  z.object({ champ, op: z.literal('vide'), type: z.literal('texte').optional() }).strict(),
  z.object({ champ, op: z.literal('rempli'), type: z.literal('texte').optional() }).strict(),
  z.object({ champ, op: z.literal('hors'), valeurs: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('parmi'), valeurs: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('moi') }).strict(),
]));
export const conditionSchema = z.unknown().superRefine((value, ctx) => {
  if (!profondeurDeclaree(value)) ctx.addIssue({ code: 'custom', message: 'condition trop profonde' });
}).pipe(arbre);

export function champsDeCondition(condition: Condition): string[] {
  if ('et' in condition) return condition.et.flatMap(champsDeCondition);
  if ('ou' in condition) return condition.ou.flatMap(champsDeCondition);
  return [condition.champ];
}

/**
 * `moi` : la personne connectee. Un champ `personne` stocke un `personId`, une
 * trace (`socle:createdBy`) stocke un uid : le test « est moi » compare chacun
 * a sa clef. Absente ou inconnue, aucune fiche n'est a moi.
 */
export interface ContexteDeCondition {
  maintenant?: Date;
  timezone?: string;
  moi?: { personId: string | null; uid: string | null } | null;
}

/**
 * LES CHAMPS QU'UNE CONDITION VEUT « MOI » ET QU'ON PEUT ECRIRE : ses feuilles
 * `moi` hors traces (l'auteur se pose seul). Une fiche creee dans une porte
 * tranchee sur moi les recoit prerempli, sinon elle n'y apparaitrait pas.
 */
export function champsMoiAEcrire(condition: Condition): string[] {
  if ('et' in condition) return condition.et.flatMap(champsMoiAEcrire);
  if ('ou' in condition) return condition.ou.flatMap(champsMoiAEcrire);
  return condition.op === 'moi' && !estTraceDeCompte(condition.champ) ? [condition.champ] : [];
}

/**
 * LES VALEURS QU'UNE CONDITION FIXE : ses feuilles `parmi` a UNE valeur,
 * atteintes par des `et` seulement (sous un `ou`, rien n'est sur). Une fiche
 * creee dans la porte « Sales » (`pole` parmi `sales`) nait avec ce pole, sinon
 * elle quitterait la porte a l'enregistrement.
 */
export function valeursFixees(condition: Condition): [string, string | number | boolean][] {
  if ('et' in condition) return condition.et.flatMap(valeursFixees);
  if ('ou' in condition) return [];
  return condition.op === 'parmi' && condition.valeurs.length === 1 && !estTraceDeCompte(condition.champ)
    ? [[condition.champ, condition.valeurs[0]!]]
    : [];
}

/**
 * UNE VALEUR DE CE CHAMP PEUT-ELLE PASSER LA CONDITION ? Lu sur les feuilles
 * `parmi` et `hors` de ce champ atteintes par des `et` ; tout le reste (un `ou`,
 * un autre champ, un autre operateur) ne dit rien et laisse passer. C'est ce
 * qui borne les colonnes d'un kanban a ce que sa porte peut montrer.
 */
export function valeurPermise(condition: Condition, champ: string, valeur: string | number | boolean): boolean {
  if ('et' in condition) return condition.et.every(c => valeurPermise(c, champ, valeur));
  if ('ou' in condition || condition.champ !== champ) return true;
  if (condition.op === 'parmi') return condition.valeurs.includes(valeur);
  if (condition.op === 'hors') return !condition.valeurs.includes(valeur);
  return true;
}

/** Le champ est-il une trace du socle (un uid), plutot qu'une personne (un `personId`) ? */
export function estTraceDeCompte(champ: string): boolean {
  return estChampDeTrace(champ.replace(/^socle:/, ''));
}
/**
 * LE CHAMP VIRTUEL `socle:piecesJointes` : la liste des pieces jointes pretes de
 * la fiche ouverte (smart boutons, `docs/socle/smart-boutons.md`). Ce n'est pas
 * un champ de la fiche : l'evaluateur le lit comme les autres, par l'acces
 * `lire` que l'appelant fournit, et c'est a l'appelant de le calculer, seulement
 * quand une condition le cite (`champsDeCondition`). Typiquement avec `rempli`.
 */
export const CHAMP_PIECES_JOINTES = 'socle:piecesJointes';

export function evaluerCondition(condition: Condition, lire: (champ: string) => unknown, contexte: ContexteDeCondition = {}): boolean {
  if ('et' in condition) return condition.et.every(c => evaluerCondition(c, lire, contexte));
  if ('ou' in condition) return condition.ou.some(c => evaluerCondition(c, lire, contexte));
  const value = lire(condition.champ);
  if (condition.op === 'hors') return !condition.valeurs.some(v => v === value);
  if (condition.op === 'parmi') return Array.isArray(value) ? value.some(v => condition.valeurs.includes(v)) : condition.valeurs.some(v => v === value);
  if (condition.op === 'moi') {
    const clef = estTraceDeCompte(condition.champ) ? contexte.moi?.uid : contexte.moi?.personId;
    if (!clef) return false;
    return Array.isArray(value) ? value.includes(clef) : value === clef;
  }
  if (condition.op === 'vide') return value == null || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0) || (condition.type === 'texte' && typeof value !== 'string');
  if (condition.op === 'rempli') return !(value == null || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0) || (condition.type === 'texte' && typeof value !== 'string'));
  const jour = jourDeValeur(value, contexte.timezone);
  if (jour === null) return false;
  const aujourdhui = jourDansFuseau(contexte.maintenant ?? new Date(), contexte.timezone);
  return condition.op === 'aujourdhui' ? jour === aujourdhui : jour < aujourdhui;
}

/**
 * LE JOUR D'UNE VALEUR, pour `avantAujourdhui` et `aujourdhui` : une date pure
 * est deja un jour, un instant (Date ou ISO) se ramene au jour dans le fuseau
 * de l'org. Absente ou illisible : `null`, et la condition ne passe pas.
 */
function jourDeValeur(value: unknown, timezone?: string): string | null {
  if (estDatePure(value)) return value;
  const instant = value instanceof Date ? value : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) ? new Date(value) : null;
  if (!instant || !Number.isFinite(instant.getTime())) return null;
  return jourDansFuseau(instant, timezone);
}

/**
 * LES LIBELLES QU'UNE PHRASE DE CONDITION EMPRUNTE a l'app : le nom lisible d'un
 * champ, et celui d'une valeur de ce champ (l'etiquette d'une liste, le nom
 * d'un statut). Fournis par l'appelant, qui connait le jeu de la porte.
 */
export interface LibellesDeCondition {
  champ: (nom: string) => string;
  valeur: (nom: string, v: string | number | boolean) => string;
}

/**
 * LA PHRASE D'UNE CONDITION, courte, pour dire POURQUOI un bouton est grise :
 * « Disponible si Etape : En consultation ». Les `et` et `ou` se joignent par
 * « et » et « ou » ; une feuille se dit selon son operateur.
 */
export function phraseDeCondition(condition: Condition, libelles: LibellesDeCondition): string {
  return `Disponible si ${phraseDeConditionNue(condition, libelles)}`;
}

function phraseDeConditionNue(condition: Condition, libelles: LibellesDeCondition): string {
  if ('et' in condition) return condition.et.map(c => phraseDeConditionNue(c, libelles)).join(' et ');
  if ('ou' in condition) return condition.ou.map(c => phraseDeConditionNue(c, libelles)).join(' ou ');
  const nom = libelles.champ(condition.champ);
  switch (condition.op) {
    case 'parmi': return `${nom} : ${condition.valeurs.map(v => libelles.valeur(condition.champ, v)).join(' ou ')}`;
    case 'hors': return `${nom} n'est pas ${condition.valeurs.map(v => libelles.valeur(condition.champ, v)).join(' ni ')}`;
    case 'vide': return `${nom} vide`;
    case 'rempli': return `${nom} rempli`;
    case 'moi': return `${nom} : moi`;
    case 'aujourdhui': return `${nom} : aujourd'hui`;
    case 'avantAujourdhui': return `${nom} avant aujourd'hui`;
  }
}
